import { useEffect, useState } from "react";
import { Card, ErrorBanner, Tile, fmt } from "../components/ui";
import { api, type PerformanceMetrics } from "../lib/api";

type Report = Awaited<ReturnType<typeof api.performance>>;
type Kind = "" | "campaign" | "blast" | "reminder";
type SortKey = "label" | keyof PerformanceMetrics;

const RANGES = [
  { days: 1, label: "1 day" },
  { days: 7, label: "7 days" },
  { days: 14, label: "14 days" },
  { days: 30, label: "30 days" },
];

function etDay(offset = 0) {
  const d = new Date(Date.now() + offset * 86_400_000);
  return d.toLocaleDateString("en-CA", { timeZone: "America/New_York" }); // YYYY-MM-DD
}

const KINDS: Array<{ kind: Kind; label: string }> = [
  { kind: "", label: "All" },
  { kind: "campaign", label: "Campaigns" },
  { kind: "blast", label: "Blasts" },
  { kind: "reminder", label: "Reminders" },
];

const COLS: Array<{ key: SortKey; label: string }> = [
  { key: "label", label: "" },
  { key: "people", label: "People" },
  { key: "texts", label: "Texts" },
  { key: "repliers", label: "Repliers" },
  { key: "reply_rate", label: "Reply rate" },
  { key: "calls", label: "Calls" },
  { key: "calls_per_1000", label: "Calls / 1,000" },
  { key: "opt_outs", label: "Opt-outs" },
  { key: "opt_out_rate", label: "Opt-out rate" },
  { key: "median_minutes_to_reply", label: "Median to reply" },
  { key: "repliers_within_1h", label: "Replied ≤1h" },
  { key: "reply_messages", label: "Reply messages" },
];

/** Minutes read badly past an hour - a median of 1,380 is most of a day. */
const took = (m: number | null) =>
  m == null
    ? "—"
    : m < 60
      ? `${Math.round(m)} min`
      : m < 1440
        ? `${(m / 60).toFixed(1)} h`
        : `${(m / 1440).toFixed(1)} d`;

export function Performance() {
  const [groupBy, setGroupBy] = useState("campaign");
  const [days, setDays] = useState(7);
  const [kind, setKind] = useState<Kind>("");
  // A named range is the common case; two dates answer "how did last Tuesday's
  // copy do" without counting backwards from today. Only one is ever in force.
  const [custom, setCustom] = useState(false);
  const [from, setFrom] = useState(() => etDay(-6));
  const [to, setTo] = useState(() => etDay());
  const backwards = custom && from > to;
  const [data, setData] = useState<Report | null>(null);
  // Kept apart from the report so the picker does not empty while a new
  // grouping is loading.
  const [dims, setDims] = useState<Report["dimensions"]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState<SortKey>("people");
  const [desc, setDesc] = useState(true);

  useEffect(() => {
    // Clicking through groupings quickly must not let a slow earlier answer
    // land on top of the one asked for last.
    let stale = false;
    if (custom && (!from || !to || backwards)) return;   // nothing to ask for yet
    // Picking a range means editing two dates, and the half-finished one in
    // between is a whole report nobody asked for - on 30 days of production
    // that is not a cheap query. Named ranges are one click, so they go now.
    const ask = () => {
    setBusy(true);
    api
      .performance({
        group_by: groupBy,
        kind: kind || undefined,
        ...(custom ? { from, to } : { days }),
      })
      .then((d) => {
        if (stale) return;
        setData(d);
        setDims(d.dimensions);
        setError(null);
      })
      .catch((e) => {
        if (!stale) setError(e.message);
      })
      .finally(() => {
        if (!stale) setBusy(false);
      });
    };
    if (!custom) {
      ask();
      return () => {
        stale = true;
      };
    }
    const t = window.setTimeout(ask, 400);
    return () => {
      stale = true;
      window.clearTimeout(t);
    };
  }, [groupBy, days, kind, custom, from, to, backwards]);

  const by = (key: SortKey) => () => {
    if (sort === key) setDesc((d) => !d);
    else {
      setSort(key);
      setDesc(key !== "label");
    }
  };
  const caret = (key: SortKey) => (sort === key ? (desc ? " ↓" : " ↑") : "");

  const rows = [...(data?.rows ?? [])].sort((a, b) => {
    if (sort === "label") {
      const c = a.label.localeCompare(b.label, undefined, { numeric: true });
      return desc ? -c : c;
    }
    const x = a[sort];
    const y = b[sort];
    // nobody replied, so no median: last whichever way round
    if (x == null || y == null) return x == null ? (y == null ? 0 : 1) : -1;
    // Number(): not every column is a number any more - enough_data is a
    // boolean, and a boolean subtraction is a type error, not a sort.
    const nx = Number(x);
    const ny = Number(y);
    return desc ? ny - nx : nx - ny;
  });

  const t = data?.totals;
  const groupLabel = dims.find((d) => d.key === groupBy)?.label ?? groupBy;

  return (
    <>
      <ErrorBanner error={error} />

      <div className="row w" style={{ marginTop: 8 }}>
        <div>
          <h2 style={{ fontSize: 19 }}>Performance</h2>
          <span className="lab">
            {data
              ? `${data.from} to ${data.to} · eastern time`
              : custom
                ? `${from} to ${to} · eastern time`
                : `last ${days} days`}
          </span>
        </div>
      </div>
      <p className="dim" style={{ marginTop: -4, fontSize: 13, maxWidth: 780 }}>
        Replies are counted as people, each credited to the last text that person
        received. Days are Eastern time.
      </p>

      <div className="row w" style={{ gap: 6 }}>
        <span className="lab">Group by</span>
        <select
          className="f"
          style={{ fontSize: 12 }}
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
        >
          {(dims.length ? dims : [{ key: groupBy, label: groupLabel }]).map((d) => (
            <option key={d.key} value={d.key}>
              {d.label}
            </option>
          ))}
        </select>
        <span className="sp" />
        {KINDS.map((k) => (
          <button
            key={k.label}
            className={`btn s${kind === k.kind ? " p" : ""}`}
            onClick={() => setKind(k.kind)}
          >
            {k.label}
          </button>
        ))}
        <span className="dim">·</span>
        {RANGES.map((r) => (
          <button
            key={r.days}
            className={`btn s${!custom && days === r.days ? " p" : ""}`}
            onClick={() => {
              setCustom(false);
              setDays(r.days);
            }}
          >
            {r.label}
          </button>
        ))}
        <button
          className={`btn s${custom ? " p" : ""}`}
          onClick={() => setCustom(true)}
          title="Any two dates, eastern time, both included"
        >
          Dates…
        </button>
        {custom && (
          <>
            <input
              className="f"
              style={{ fontSize: 12 }}
              type="date"
              value={from}
              max={etDay()}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="From"
            />
            <span className="dim">to</span>
            <input
              className="f"
              style={{ fontSize: 12 }}
              type="date"
              value={to}
              max={etDay()}
              onChange={(e) => setTo(e.target.value)}
              aria-label="To"
            />
          </>
        )}
      </div>
      {backwards && (
        <p className="lab" style={{ color: "var(--bad)", marginTop: 4 }}>
          The first date is after the last one — nothing to report.
        </p>
      )}

      {t && (
        <div className="tiles">
          <Tile
            label="People texted"
            value={fmt.n(t.people)}
            detail={`${fmt.n(t.texts)} texts`}
          />
          <Tile
            label="Repliers"
            value={fmt.n(t.repliers)}
            detail={`${fmt.pct(t.reply_rate)} of people texted`}
            tone="ok"
          />
          <Tile
            label="Opt-outs"
            value={fmt.n(t.opt_outs)}
            detail={`${t.opt_out_rate.toFixed(2)}% of people texted`}
            tone={t.opt_out_rate > 2 ? "ba" : undefined}
          />
          <Tile
            label="Median time to reply"
            value={took(t.median_minutes_to_reply)}
            detail={`${fmt.n(t.repliers_within_1h)} within an hour`}
          />
        </div>
      )}

      <Card
        title={busy ? "Working…" : `By ${groupLabel.toLowerCase()}`}
        footer={
          <span className="dim">
            Reply rate is repliers over people texted. Reply messages is every message
            credited, follow-ups in the same conversation included.
          </span>
        }
      >
        {!data ? (
          <div className="empty">{busy ? "Counting replies…" : "Nothing to show."}</div>
        ) : rows.length === 0 ? (
          <div className="empty">Nothing sent in this window, so there is nothing to credit.</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  {COLS.map((c) => (
                    <th key={c.key} className="sortable" onClick={by(c.key)}>
                      {c.label || groupLabel}
                      {caret(c.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.key ? JSON.stringify(r.key) : i}>
                    <td>
                      <b>{r.label}</b>
                      {r.detail && (
                        <div
                          className="dim"
                          title={r.detail}
                          style={{
                            fontSize: 11,
                            maxWidth: 360,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {r.detail}
                        </div>
                      )}
                    </td>
                    <td className="mono">{fmt.n(r.people)}</td>
                    <td className="mono dim">{fmt.n(r.texts)}</td>
                    <td className="mono">{fmt.n(r.repliers)}</td>
                    <td className="mono">
                      {/* The rate alone invites a conclusion the numbers may
                          not support. On 23 Sep 2026 5.2% against 2.9% was
                          read as a 1.8x lift; by the close it had reversed to
                          13.3% against 14.4%. Neither gap was ever readable.
                          So a thin row says so, and shows its real range. */}
                      <b className={r.enough_data === false ? "dim" : undefined}>
                        {fmt.pct(r.reply_rate)}
                      </b>
                      {r.reply_rate_low != null && r.reply_rate_high != null && (
                        <div
                          className="dim"
                          style={{ fontSize: 11, whiteSpace: "nowrap" }}
                          title={
                            r.enough_data === false
                              ? `Only ${r.repliers} replies - too few to draw a conclusion. The true rate is somewhere between ${r.reply_rate_low}% and ${r.reply_rate_high}%.`
                              : `95% confident the true rate is between ${r.reply_rate_low}% and ${r.reply_rate_high}%`
                          }
                        >
                          {r.enough_data === false ? "± too few · " : ""}
                          {r.reply_rate_low}–{r.reply_rate_high}%
                        </div>
                      )}
                    </td>
                    <td className="mono">{fmt.n(r.calls)}</td>
                    <td className="mono">
                      {/* Number(): a count that arrives as a Decimal is
                          serialised as a string, and a string has no
                          .toFixed - which took the whole report down once. */}
                      <b>{Number(r.calls_per_1000) ? Number(r.calls_per_1000).toFixed(2) : "—"}</b>
                    </td>
                    <td className="mono dim">{fmt.n(r.opt_outs)}</td>
                    <td className="mono">
                      <span style={{ color: r.opt_out_rate > 2 ? "var(--bad)" : undefined }}>
                        {r.opt_out_rate.toFixed(2)}%
                      </span>
                    </td>
                    <td className="mono">{took(r.median_minutes_to_reply)}</td>
                    <td className="mono">{fmt.n(r.repliers_within_1h)}</td>
                    <td className="mono dim">{fmt.n(r.reply_messages)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
